import { colors } from "../styles/colors";
import { Link } from "react-router-dom";
import { ArrowRight, Home } from "lucide-react";
import SponsorPlaceholder from "../components/SponsorPlaceholder";
import ScrollToTop from "../components/ScrollToTop";

export default function WarrantiesAndRepresentations() {
  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.background }}>
      <header className="px-6 md:px-12 lg:px-24 py-6 " style={{ borderColor: colors.border }}>
        <Link to="/" className="inline-flex items-center gap-2 text-lg font-medium hover:opacity-80 transition-opacity" style={{ color: colors.accent }}>
          <img src="/clauseclear-logo.png" alt="ClauseClear" className="h-10 object-contain" />

        </Link>
      </header>

      <article className="px-6 md:px-12 lg:px-24 py-16">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-light tracking-tight leading-[1.1] mb-8" style={{ color: colors.text }}>
            Plain-English Explanation of Warranties and Representations
          </h1>

          <p className="text-base md:text-lg leading-relaxed mb-12" style={{ color: colors.textMuted }}>
            Warranties and representations are promises about facts, quality, or capabilities that form the basis of the contract.
          </p>

          <section className="mb-12 rounded-2xl p-8" style={{ backgroundColor: colors.backgroundLight, border: `1px solid ${colors.border}` }} style={{ borderColor: colors.border }}>
            <h2 className="text-2xl font-medium mb-4" style={{ color: colors.accent }}>Plain-English Explanation</h2>
            <p className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
              These are factual statements you're guaranteeing are true. Representations are statements made at contract signing. Warranties are ongoing promises about quality, ownership, or capabilities. If they turn out false, the other party may have grounds to terminate or claim damages.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-medium mb-4" style={{ color: colors.accent }}>Why This Clause Exists</h2>
            <p className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
              Warranties create accountability for factual claims and establish baseline expectations. They allocate risk for inaccurate information and provide remedies when promises aren't kept. Clear warranties reduce disputes about what was actually promised versus assumed.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-medium mb-4" style={{ color: colors.accent }}>Typical Effects</h2>
            <ul className="space-y-3 ml-6">
              <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                You're legally bound by the truth of statements you make
              </li>
              <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                False warranties can lead to contract termination or damages
              </li>
              <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                May include ongoing obligations to maintain certain standards
              </li>
              <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                Common warranties include ownership, authority to contract, and non-infringement
              </li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-medium mb-4" style={{ color: colors.accent }}>What to Watch Out For</h2>
            <p className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
              Only warrant what you can genuinely guarantee. Avoid absolute warranties about future performance or third-party behavior. Check whether warranties survive contract termination and how long liability lasts. Consider insurance for warranties that create significant exposure.
            </p>
          </section>

          <section className="mb-12 rounded-2xl p-8" style={{ backgroundColor: colors.backgroundLight, border: `1px solid ${colors.border}` }} style={{ borderColor: colors.border }}>
            <h2 className="text-2xl font-medium mb-4" style={{ color: colors.accent }}>Important Context</h2>
            <p className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
              Express warranties override implied warranties in many jurisdictions. Some warranties are implied by law even without written clauses. Disclaimers of warranties must often use specific language to be effective. This explanation provides general information but doesn't constitute legal advice about your specific situation.
            </p>
          </section>

          <section className="mb-12">
            <div className="flex gap-4 mb-8">
              <Link to="/clauses" className="text-base font-medium hover:opacity-80 transition-opacity" style={{ color: colors.accent }}>
                ← All Clauses
              </Link>
              <Link to="/clauses/business" className="text-base font-medium hover:opacity-80 transition-opacity" style={{ color: colors.accent }}>
                Business Clauses
              </Link>
            </div>
            <h2 className="text-2xl font-medium mb-6" style={{ color: colors.accent }}>Related Clauses</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Link to="/explain-indemnity-clause" className="p-6 rounded-xl hover:shadow-md transition-all" style={{ borderColor: colors.border }}>
                <h3 className="font-medium mb-2" style={{ color: colors.accent }}>Indemnity</h3>
                <p className="text-sm" style={{ color: colors.textMuted }}>Learn about liability protection</p>
              </Link>
              <Link to="/explain-limitation-of-liability-clause" className="p-6 rounded-xl hover:shadow-md transition-all" style={{ borderColor: colors.border }}>
                <h3 className="font-medium mb-2" style={{ color: colors.accent }}>Limitation of Liability</h3>
                <p className="text-sm" style={{ color: colors.textMuted }}>Understand liability caps</p>
              </Link>
            </div>
          </section>

          <div className="text-center p-8 rounded-2xl" style={{ borderColor: colors.border }}>
            <p className="text-lg mb-4" style={{ color: colors.textMuted }}>Want to understand your own clause?</p>
            <Link to="/" className="inline-flex items-center gap-2 text-white hover:opacity-90 rounded-full px-8 py-4 text-lg font-medium transition-all" style={{ backgroundColor: '#2F3E30' }}>
              Try ClauseClear
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>

          {/* Sponsorship Placeholder */}
          <SponsorPlaceholder variant="full" />
        </div>
      </article>

      <footer className="px-6 md:px-12 lg:px-24 py-12 text-center " style={{ borderColor: colors.border }}>
        <p className="text-sm mb-2" style={{ color: colors.textMuted }}>ClauseClear is an educational tool. It does not provide legal advice.</p>
        <a href="https://buymeacoffee.com/jamesmoneyz" target="_blank" rel="noopener noreferrer" className="text-base font-medium hover:opacity-80 transition-opacity" style={{ color: colors.accent }}>
          Support the project
        </a>
      </footer>
    </div>
  );
}
